import React, { useEffect, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

import SecondaryMenu from '../../menu/secondaryMenu.component';
import PeriodDropdown from '../../shared/periodDropdown.component';

import { PeriodPropTypes } from '../../shared/propTypes/period';
import { OptionsPropType } from '../../shared/propTypes/common';

import { TabTypes } from '../shared/constants';
import HamburgerDropdown from '../shared/hamburgerDropdown.component';
import AdminEntityImport from '../shared/adminEntityImport.component';

import Config from '../../config';

import ObligationsGrid from './obligationsGrid.component';
import { ObligationPropTypes } from './propTypes';

import './_obligationsPage.style.scss';


import {
  fetchObligations,
  fetchUploadedObligations,
  fetchLookUpDataForObligations,
  updateObligation,
  createObligation,
  uploadObligations,
  importObligations,
  importObligationsSuccess,
  importObligationsFailed,
  clearUploadedObligations,
  hideObligationModalForDelete,
  removeObligation,
  fetchUserAssignedCompanyCodes,
} from './store/actions';

import {
  periodOrCurrentSelector,
  environmentSuffixSelector,
  companyCodesSetSelector,
} from '../../shared/store/selectors';

import { hideDecimalsSelector, negativeMinusSelector, displayZeroSelector } from '../../userData/store/selectors';

import {
  obligationsSelector,
  isLoadingObligationsSelector,
  isLoadingUploadedObligationsSelector,
  lookUpDataSelector,
  isLoadingLookUpDataSelector,
  isUpdatingObligationsSelector,
  isCreatingObligationsSelector,
  obligationBasedInitialDataSelector,
  uploadedObligationsSelector,
  isImportingObligationsSelector,
  canModifyAllObligationsSelector,
  canEditTaxCurrencySubledgerSelector,
  isRemovingObligationSelector,
  companyCodeBasedInitialDataSelector,
  isLoadingUserAssignedCompanyCodesSelector,
} from './store/selectors';

import {
  idOfLastJobOfTypeSelectorFactory,
  isLastJobOfTypeFinishedSelectorFactory,
  progressOfLastJobOfTypeSelectorFactory,
  isLastJobOfTypeSucceededSelectorFactory,
} from '../../shared/jim/store/selectors';

import {
  NOLOptions,
  RecordVAOptions,
  SecondaryTaxOptions,
  StatePrePostNOLOptions,
  EnabledFlagOptions,
  FederalFTCLimitOptions,
  RecordFTCOptions,
  ModalNames,
} from './constants';

import { withPermissions, permissions } from '../../shared/authorization';

import downloadXlsx from './downloadXlsx';
import { fromSearchString } from '../../shared/urlUtils';
import obligationColumnDefinitionsForImport from '../../shared/columnDefinitions/obligations';
import { modalDataSelector } from '../../shared/modals/modals.selectors';

import JimJobInfo from '../../shared/jim/jimJobInfo.component';
import { useDeleteJobResults } from '../../shared/mutations/jimJobs';
import { ViewImportTabs } from '../shared/ViewImportTabs.component';
import { useJobResults } from '../../shared/queries/jobs';
import { ImportInPreviousPeriodWarning } from '../shared/ImportInPreviousPeriodWarning.component';
import { useRateChangesToUpdateAfterImportCount } from './useRateChangesToUpdateAfterImportCount.hook';
import { PureAlert as Alert } from '../../shared/alert.component';
import { getTaxRateToUpdateMessage, getUnapprovedEntryMessage } from './helpers';

const progressSelector = progressOfLastJobOfTypeSelectorFactory(
  Config.jimJobs.obligationsValidate.type
);
const isLastValidateJobFinishedSelector = isLastJobOfTypeFinishedSelectorFactory(
  Config.jimJobs.obligationsValidate.type
);
const isLastValidateJobSucceededSelector = isLastJobOfTypeSucceededSelectorFactory(
  Config.jimJobs.obligationsValidate.type
);
const isLastImportJobFinishedSelector = isLastJobOfTypeFinishedSelectorFactory(
  Config.jimJobs.obligationsImport.type
);
const idOfLastObligationsValidateJobSelector = idOfLastJobOfTypeSelectorFactory(
  Config.jimJobs.obligationsValidate.type
);

const ignoredFields = ['countryCode', 'companyCode', 'countryTaxType'];

const ObligationsEditViewPage = props => {
  const {
    period,
    environmentSuffix,
    hideDecimals,
    negativeMinus,
    shouldDisplayZero,

    obligations,
    uploadedObligations,

    fetchObligations,
    fetchUploadedObligations,
    isLoadingObligations,
    isLoadingUploadedObligations,

    fetchLookUpDataForObligations,
    isLoadingLookUpData,
    fetchedLookUpData,

    fetchUserAssignedCompanyCodes,
    isLoadingUserAssignedCompanyCodes,

    companyCodeBasedInitialData,
    obligationBasedInitialData,

    updateObligation,
    isUpdatingObligations,

    createObligation,
    isCreatingObligations,

    canModifyInSelectedPeriod,
    canModifyAllObligations,
    companyCodesSet,
    canEditTaxCurrencySubledger,

    uploadObligations,
    importObligations,
    importObligationsSuccess,
    importObligationsFailed,
    clearUploadedObligations,

    isImportingObligations,

    deleteModalData,
    hideObligationModalForDelete,
    removeObligation,
    isRemovingObligation,

    isProcessingObligationsValidate,
    isObligationValidateSucceeded,
    isProcessingObligationsImport,
    idOfLastObligationsValidateJob,
  } = props;

  const { search } = useLocation();
  const initialFilters = useMemo(() => ({
    companyCode: fromSearchString(search, true)?.companyCode,
  }), [search]);


  useEffect(() => {
    if (!isProcessingObligationsImport) {
      fetchObligations(period.name);
      fetchLookUpDataForObligations(period.name);
      fetchUserAssignedCompanyCodes(period.name);
    }
  }, [
    period,
    isProcessingObligationsImport,
    fetchLookUpDataForObligations,
    fetchObligations,
    fetchUserAssignedCompanyCodes,
  ]);

  const {
    data: validateJobResults,
    isLoading: isLoadingValidateJobResults,
    refetch: refetchValidateJobResults,
  } = useJobResults({
    jobId: idOfLastObligationsValidateJob,
    enabled: isProcessingObligationsValidate,
  });

  useEffect(() => {
    if (!isProcessingObligationsValidate && idOfLastObligationsValidateJob) {
      refetchValidateJobResults(idOfLastObligationsValidateJob);
    }
  }, [period, isProcessingObligationsValidate, idOfLastObligationsValidateJob, refetchValidateJobResults]);

  useEffect(() => {
    if (!isProcessingObligationsValidate && idOfLastObligationsValidateJob) {
      fetchUploadedObligations(idOfLastObligationsValidateJob);
    }
  }, [period, isProcessingObligationsValidate, idOfLastObligationsValidateJob, fetchUploadedObligations]);

  const update = useCallback(async (obligation, options = {}) => {
    await updateObligation(obligation, { periodName: period.name }, options);
  }, [updateObligation, period.name]);

  const create = useCallback(async (obligation, options = {}) => {
    await createObligation(obligation, { periodName: period.name }, options);
  }, [createObligation, period.name]);

  const [activeTab, setActiveTab] = useState(TabTypes.VIEW);

  const generateTemplateFileName = useCallback(
    ({ periodName }) => `TLS_Obligations_${periodName}`,
    []);

  const fileToImportChange = useCallback(prop => {
    const file = prop.target ? prop.target.files[0] : prop;
    uploadObligations(file, period, obligations);
  }, [uploadObligations, period, obligations]);

  const fileToImportSelect = useCallback(hideDropdown => () => {
    setActiveTab(TabTypes.IMPORT);
    hideDropdown();
  }, []);

  const templateFileName = `${generateTemplateFileName({ periodName: period.name })}${environmentSuffix}.xlsx`;

  const importData = useCallback(
    async () => {
      await importObligations(
        idOfLastObligationsValidateJob,
        period
      );
      clearUploadedObligations();
      setActiveTab(TabTypes.VIEW);
      fetchObligations(period.name);
    },
    [
      period,
      importObligations,
      clearUploadedObligations,
      setActiveTab,
      fetchObligations,
      idOfLastObligationsValidateJob,
    ]
  );

  const exportToExcel = useCallback(callback => {
    if (obligations.length) {
      downloadXlsx({
        periodName: period.name,
        obligations,
        hideDecimals,
        negativeMinus,
        shouldDisplayZero,
        environmentSuffix,
      });
    }
    if (callback) {
      callback();
    }
  }, [
    period,
    obligations,
    hideDecimals,
    negativeMinus,
    shouldDisplayZero,
    environmentSuffix,
  ]);

  const idFieldComparator = useCallback((obligation, importedObligation) => (
    obligation.companyCode === importedObligation.companyCode
    && obligation.countryCode === importedObligation.countryCode
    && obligation.countryTaxType === importedObligation.countryTaxType
  ), []);

  const uploadedEntities = isObligationValidateSucceeded && uploadedObligations
    ? uploadedObligations
    : [];

  const {
    taxRatesToUpdate,
    unapprovedEntryCount,
    isLoadingRateChangesToUpdate,
  } = useRateChangesToUpdateAfterImportCount({
    uploadedEntities,
    existingEntities: obligations,
  });

  const isUploadingEntities = isImportingObligations
    || isProcessingObligationsValidate
    || isLoadingRateChangesToUpdate
    || isLoadingUploadedObligations;

  const {
    mutate: deleteUploadedObligations,
    isLoading: isDeletingUploadedObligations,
  } = useDeleteJobResults({
    jobId: idOfLastObligationsValidateJob,
    successMessage: 'Successfully discarded uploaded obligations',
    errorMessage: 'An error occurred during the discard of uploaded obligations. Please try again.',
  });

  const renderUploadTab = () => (
    <div>
      <JimJobInfo
        jobConfig={Config.jimJobs.obligationsValidate}
        showProgressBar
      />
      <ImportInPreviousPeriodWarning
        jobResults={validateJobResults}
        showWarning={!isLoadingValidateJobResults || !isUploadingEntities}
      />
      { (taxRatesToUpdate > 0 || unapprovedEntryCount > 0) && (
        <Alert
          content={(
            <>
              { taxRatesToUpdate > 0 && <div>{ getTaxRateToUpdateMessage(taxRatesToUpdate) }</div>}
              { unapprovedEntryCount > 0 && <div>{ getUnapprovedEntryMessage(unapprovedEntryCount)} </div>}
            </>
          )}
          type="alert-info"
          show={!!taxRatesToUpdate}
        />
      )}
      <AdminEntityImport
        entities={obligations}
        uploadedEntities={uploadedEntities}
        uploadingEntities={isUploadingEntities}
        selectFileToUpload={fileToImportChange}
        importEntities={importData}
        columnDefs={obligationColumnDefinitionsForImport}
        templateFileName={templateFileName}
        templatePath={Config.templates.OBLIGATIONS}
        pageTitle="Obligations"
        idField="countryCode"
        idFieldComparator={idFieldComparator}
        ignoredFields={ignoredFields}
        onDiscard={deleteUploadedObligations}
        isSubmitDisabled={isDeletingUploadedObligations}
        promptOnExit={activeTab === TabTypes.IMPORT}
        disableImporting={isLoadingLookUpData || isLoadingObligations}
      />
    </div>
  );

  const renderEditViewGrid = () => (
    <div>
      <JimJobInfo
        jobConfig={Config.jimJobs.obligationsImport}
        onJobSuccess={importObligationsSuccess}
        onJobFail={importObligationsFailed}
        showProgressBar
      />
      <div className="obligations-page">
        <ObligationsGrid
          period={period}
          obligations={obligations}
          isLoadingObligations={isLoadingObligations || isLoadingUserAssignedCompanyCodes}
          isLoadingLookUpData={isLoadingLookUpData}
          lookUpData={{
            ...fetchedLookUpData,
            obligationBasedInitialData,
            companyCodeBasedInitialData,
            NOLOptions,
            RecordVAOptions,
            SecondaryTaxOptions,
            StatePrePostNOLOptions,
            EnabledFlagOptions,
            FederalFTCLimitOptions,
            RecordFTCOptions,
          }}
          updateObligation={update}
          isUpdatingObligations={isUpdatingObligations}
          createObligation={create}
          isCreatingObligations={isCreatingObligations}

          canModifyAllObligations={canModifyAllObligations}
          companyCodesSet={companyCodesSet}
          canModifyInSelectedPeriod={canModifyInSelectedPeriod}
          canEditTaxCurrencySubledger={canEditTaxCurrencySubledger}

          deleteModalData={deleteModalData}
          hideObligationModalForDelete={hideObligationModalForDelete}
          removeObligation={removeObligation}
          isRemovingObligation={isRemovingObligation}

          initialFilters={initialFilters}
        />
      </div>
    </div>
  );

  return (
    <div className="ag-grid-full-page-with-secondary-menu with-editable-grid admin-import-page">
      <SecondaryMenu>
        <PeriodDropdown />
        <div className="hamburger-dropdown ml-auto text-right">
          <HamburgerDropdown
            canModify={canModifyInSelectedPeriod}
            disableAddingEntity
            fileToImportChange={fileToImportChange}
            fileToImportSelect={fileToImportSelect}
            templatePath={Config.templates.OBLIGATIONS}
            exportToExcel={exportToExcel}
            templateFileName={templateFileName}
            disableImporting={isLoadingLookUpData || isLoadingObligations}
            disableExporting={isLoadingLookUpData || isLoadingObligations}
          />
        </div>
      </SecondaryMenu>
      <div className="page">
        <div className="container-fluid">
          <div className="row no-gutters page-content-container">
            <div className="col">
              <div className="d-flex flex-row justify-content-between">
                <h5>Obligations</h5>
              </div>
            </div>
          </div>
          <ViewImportTabs
            canModify={canModifyInSelectedPeriod}
            activeTab={activeTab}
            changeTab={setActiveTab}
            renderViewTab={renderEditViewGrid} // eslint-disable-line react/jsx-no-bind
            renderImportTab={renderUploadTab} // eslint-disable-line react/jsx-no-bind
            viewTabOnly={!canModifyInSelectedPeriod}
          />
        </div>
      </div>
    </div>
  );
};


ObligationsEditViewPage.propTypes = {
  period: PeriodPropTypes,
  canModifyInSelectedPeriod: PropTypes.bool.isRequired,
  environmentSuffix: PropTypes.string.isRequired,
  shouldDisplayZero: PropTypes.bool.isRequired,
  hideDecimals: PropTypes.bool.isRequired,
  negativeMinus: PropTypes.bool.isRequired,

  obligations: PropTypes.arrayOf(ObligationPropTypes).isRequired,
  fetchObligations: PropTypes.func.isRequired,
  uploadedObligations: PropTypes.arrayOf(ObligationPropTypes).isRequired,
  fetchUploadedObligations: PropTypes.func.isRequired,
  isLoadingObligations: PropTypes.bool.isRequired,
  isLoadingUploadedObligations: PropTypes.bool.isRequired,

  fetchUserAssignedCompanyCodes: PropTypes.func.isRequired,
  isLoadingUserAssignedCompanyCodes: PropTypes.bool.isRequired,

  fetchLookUpDataForObligations: PropTypes.func.isRequired,
  isLoadingLookUpData: PropTypes.bool.isRequired,

  fetchedLookUpData: PropTypes.shape({
    chartNameOptions: OptionsPropType,
    usTaxTypeOptions: OptionsPropType,
    localTaxTypeOptions: OptionsPropType,
    currencyCodeOptions: OptionsPropType,
    connectionOptions: OptionsPropType,
    companyCodes: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      subledgerFlag: PropTypes.bool.isRequired,
    })),
    countryTaxTypes: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      countryCode: PropTypes.string,
      taxRate: PropTypes.number.isRequired,
      defTaxRate: PropTypes.number.isRequired,
    })),
  }).isRequired,

  companyCodeBasedInitialData: PropTypes.objectOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    connection: PropTypes.string,
    chartName: PropTypes.string,
    functionalCurrency: PropTypes.string.isRequired,
    statCurrency: PropTypes.string,
    recordFTC: PropTypes.string,
  })),
  obligationBasedInitialData: PropTypes.objectOf(ObligationPropTypes),

  updateObligation: PropTypes.func.isRequired,
  isUpdatingObligations: PropTypes.bool.isRequired,

  createObligation: PropTypes.func.isRequired,
  isCreatingObligations: PropTypes.bool.isRequired,

  uploadObligations: PropTypes.func.isRequired,
  importObligations: PropTypes.func.isRequired,
  importObligationsSuccess: PropTypes.func.isRequired,
  importObligationsFailed: PropTypes.func.isRequired,
  clearUploadedObligations: PropTypes.func.isRequired,

  isImportingObligations: PropTypes.bool.isRequired,

  canModifyAllObligations: PropTypes.bool.isRequired,
  companyCodesSet: PropTypes.instanceOf(Set).isRequired,
  canEditTaxCurrencySubledger: PropTypes.bool.isRequired,

  deleteModalData: PropTypes.shape({
    show: PropTypes.bool.isRequired,
    data: PropTypes.shape({
      rowIndex: PropTypes.number,
      entity: ObligationPropTypes,
    }),
  }),
  hideObligationModalForDelete: PropTypes.func.isRequired,
  removeObligation: PropTypes.func.isRequired,
  isRemovingObligation: PropTypes.bool.isRequired,

  obligationsValidateProgress: PropTypes.number,
  isProcessingObligationsValidate: PropTypes.bool,
  isObligationValidateSucceeded: PropTypes.bool,
  isProcessingObligationsImport: PropTypes.bool,
  idOfLastObligationsValidateJob: PropTypes.string,
};


const mapStateToProps = state => ({
  period: periodOrCurrentSelector(state),
  environmentSuffix: environmentSuffixSelector(state),
  shouldDisplayZero: displayZeroSelector(state),
  hideDecimals: hideDecimalsSelector(state),
  negativeMinus: negativeMinusSelector(state),

  deleteModalData: modalDataSelector(state, ModalNames.obligationDeleteModal),

  obligations: obligationsSelector(state),
  uploadedObligations: uploadedObligationsSelector(state),
  isLoadingObligations: isLoadingObligationsSelector(state),
  isLoadingUploadedObligations: isLoadingUploadedObligationsSelector(state),

  fetchedLookUpData: lookUpDataSelector(state),
  isLoadingLookUpData: isLoadingLookUpDataSelector(state),

  obligationBasedInitialData: obligationBasedInitialDataSelector(state),
  companyCodeBasedInitialData: companyCodeBasedInitialDataSelector(state),

  isUpdatingObligations: isUpdatingObligationsSelector(state),
  isCreatingObligations: isCreatingObligationsSelector(state),
  isImportingObligations: isImportingObligationsSelector(state),

  companyCodesSet: companyCodesSetSelector(state),
  canModifyAllObligations: canModifyAllObligationsSelector(state),
  canEditTaxCurrencySubledger: canEditTaxCurrencySubledgerSelector(state),

  isRemovingObligation: isRemovingObligationSelector(state),

  isLoadingUserAssignedCompanyCodes: isLoadingUserAssignedCompanyCodesSelector(state),

  obligationsValidateProgress: progressSelector(state),
  isProcessingObligationsValidate: !isLastValidateJobFinishedSelector(state),
  isProcessingObligationsImport: !isLastImportJobFinishedSelector(state),
  isObligationValidateSucceeded: isLastValidateJobSucceededSelector(state),
  idOfLastObligationsValidateJob: idOfLastObligationsValidateJobSelector(state),
});

export default withPermissions(permissions.OBLIGATIONS)(
  connect(mapStateToProps, {
    fetchObligations,
    fetchUserAssignedCompanyCodes,
    fetchUploadedObligations,
    fetchLookUpDataForObligations,
    updateObligation,
    createObligation,
    uploadObligations,
    importObligations,
    importObligationsSuccess,
    importObligationsFailed,
    clearUploadedObligations,
    removeObligation,
    hideObligationModalForDelete,
  })(ObligationsEditViewPage)
);
